import { PaginationState, SortingState } from "@tanstack/react-table";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { fields } from "./Fields";
import { TableView } from "@components/common/TableView";
import { Spinner } from "@thetreep/ui";
import { Caisse, CaisseIdentity, ListResponse } from "@gen/caisse/v1/caisse_pb";
import { createCaisse, getCaisses, getCaisseIds } from "@services/caisse";
import { CreationForm } from "@components/common/CreationForm";
import { Agency } from "@gen/agency/v1/agency_pb";
import { getAgencies } from "@services/agency";
import { useToaster } from "@hooks/useToaster";
import { ConnectError } from "@connectrpc/connect";
import { useNavigate } from "react-router";
import { Role } from "@gen/user/v1/user_pb";
import { useUserStore } from "@stores/userStore";
import { toSort } from "@services/common";

export type ConfigList = { value: string; label: string }[];
export type Config = { config: string };

export function CaisseManagement() {
  const [isCreationOpen, setIsCreationOpen] = useState(false);
  const [caisses, setCaisses] = useState<ListResponse>();
  const [agencies, setAgencies] = useState<Agency[]>([]);
  const [caisseIds, setCaisseIds] = useState<CaisseIdentity[]>();
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [sorting, setSorting] = useState<SortingState>([]);
  const [isLoadingCaisses, setIsLoadingCaisses] = useState(false);
  const [isCreationLoading, setIsCreationLoading] = useState(false);
  const { t } = useTranslation();
  const { addToast } = useToaster();
  const navigate = useNavigate();
  const { user } = useUserStore();

  async function fetchCaisses() {
    setIsLoadingCaisses(true);
    try {
      const caisses = await getCaisses(
        pagination.pageIndex + 1,
        pagination.pageSize,
        toSort(sorting),
      );
      setCaisses(caisses);
    } catch (error) {
      console.error("Error fetching caisses:", error);
    } finally {
      setIsLoadingCaisses(false);
    }
  }

  async function submitCaisse(caisse: Caisse) {
    setIsCreationLoading(true);
    try {
      await createCaisse(caisse);
      addToast("success", t("admin.caisse.caisseCreated"));
      setIsCreationOpen(false);
    } catch (e) {
      if (e instanceof ConnectError) {
        addToast("error", e.rawMessage);
      }
    }
    setIsCreationLoading(false);
    fetchCaisses();
  }

  useEffect(() => {
    // oxlint-disable-next-line react-hooks/set-state-in-effect
    fetchCaisses();
  }, [pagination, sorting, isCreationOpen]);

  useEffect(() => {
    (async () => {
      try {
        setAgencies((await getAgencies()).agencies);
        setCaisseIds((await getCaisseIds()).identities);
      } catch (error) {
        console.error("Error fetching agencies or caisse IDs:", error);
      }
    })();
  }, []);

  if (!caisses || !caisseIds) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spinner />
      </div>
    );
  }

  return (
    <>
      <TableView
        fields={fields(t, agencies, caisseIds)}
        isLoadingItems={isLoadingCaisses}
        items={caisses?.caisses}
        title={t("admin.caisse.caisseManagement")}
        pagination={pagination}
        sorting={sorting}
        totalItems={Number(caisses.page?.totalElements)}
        setPagination={setPagination}
        setSorting={setSorting}
        refresh={() => fetchCaisses()}
        topActions={
          user && [Role.ADMIN, Role.TRAVEL_AGENT].includes(user.role)
            ? [
                {
                  label: t("admin.caisse.caisseOnboarding"),
                  icon: "add_line",
                  do: () => setIsCreationOpen(true),
                },
              ]
            : []
        }
        rowActions={(row) => [
          {
            label: t("details"),
            icon: "eye_line",
            do: () => navigate(`/admin/caisses/${row.id}`),
          },
          ...(user && [Role.ADMIN, Role.TRAVEL_AGENT].includes(user.role)
            ? [
                {
                  label: t("edit"),
                  icon: "pencil_line",
                  do: () => navigate(`/admin/caisses/${row.id}?edit`),
                },
              ]
            : []),
        ]}
      />
      <CreationForm
        isLoading={isCreationLoading}
        title={t("admin.caisse.caisseOnboarding")}
        fields={fields(t, agencies, caisseIds)}
        isOpen={isCreationOpen}
        onClose={() => setIsCreationOpen(false)}
        onSubmit={(value) => {
          submitCaisse(value);
        }}
      />
    </>
  );
}
